/** ISO date YYYY-MM-DD helpers for passenger date of birth. */

export function normalizeDateOfBirth(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return undefined;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return undefined;
  const date = new Date(Date.UTC(y, mo - 1, d));
  if (Number.isNaN(date.getTime())) return undefined;
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== mo - 1 || date.getUTCDate() !== d) return undefined;
  return `${m[1]}-${m[2]}-${m[3]}`;
}

function parseReferenceDate(travelDate?: string): Date {
  const iso = travelDate?.trim();
  if (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const d = new Date(`${iso}T12:00:00Z`);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return new Date();
}

/** Suggested DOB from age using the same month/day as the reference date (usually outbound travel date). */
export function approximateDateOfBirthFromAge(age: number, referenceTravelDate?: string): string {
  const n = Math.round(age);
  const ref = parseReferenceDate(referenceTravelDate);
  const y = ref.getUTCFullYear() - n;
  const mo = String(ref.getUTCMonth() + 1).padStart(2, '0');
  const day = String(ref.getUTCDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

export function formatDateOfBirthDisplay(iso?: string): string {
  const norm = normalizeDateOfBirth(iso);
  if (!norm) return '—';
  const d = new Date(`${norm}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return norm;
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function ageYearsAtReference(iso: string, referenceTravelDate?: string): number | undefined {
  const norm = normalizeDateOfBirth(iso);
  if (!norm) return undefined;
  const birth = new Date(`${norm}T12:00:00Z`);
  const ref = parseReferenceDate(referenceTravelDate);
  if (Number.isNaN(birth.getTime())) return undefined;
  let years = ref.getUTCFullYear() - birth.getUTCFullYear();
  const refMonth = ref.getUTCMonth();
  const birthMonth = birth.getUTCMonth();
  if (refMonth < birthMonth || (refMonth === birthMonth && ref.getUTCDate() < birth.getUTCDate())) {
    years -= 1;
  }
  return years >= 0 && years <= 120 ? years : undefined;
}

/** Payment / admin display: compute age (years) at the reference travel date. */
export function ageFromDateOfBirth(iso: string, referenceTravelDate?: string): number | undefined {
  return ageYearsAtReference(iso, referenceTravelDate);
}

/** Payment / admin display: ISO date plus age when known. */
export function formatPassengerDobLine(
  passenger: { age?: number; dateOfBirth?: string },
  referenceTravelDate?: string,
): string {
  const iso = resolvePassengerDateOfBirth(passenger, referenceTravelDate);
  if (!iso) {
    if (passenger.age != null && passenger.age >= 0) return `age ${passenger.age}`;
    return '';
  }
  const age = passenger.age ?? ageYearsAtReference(iso, referenceTravelDate);
  if (age != null && age >= 0) return `${iso} (age ${age})`;
  return iso;
}

export function resolvePassengerDateOfBirth(
  passenger: { age?: number; dateOfBirth?: string },
  referenceTravelDate?: string,
): string | undefined {
  const stored = normalizeDateOfBirth(passenger.dateOfBirth);
  if (stored) return stored;
  const age = passenger.age;
  if (age != null && age >= 1 && age <= 120) {
    return approximateDateOfBirthFromAge(age, referenceTravelDate);
  }
  return undefined;
}
